// Titles, descriptions, social cards and JSON-LD for every public route.
//
// Used twice: in the browser, where the router applies it after each page
// renders, and at build time (vite.config.js), where it is written into a
// static HTML file per route so crawlers that do not run JavaScript -- the
// WhatsApp, Facebook and LinkedIn link previewers -- still see the right
// card. Both call pageMeta(), so the two cannot disagree.
//
// No imports beyond pages.js and nothing touching the DOM at module scope:
// Node loads this file during the build.
import { PAGES, visiblePages } from './pages.js';

// The canonical origin. Every other host (www, the web.app domains) points
// its canonical links here.
export const SITE_URL = 'https://janathahrudayalayafoundation.com';

const DEFAULT_IMAGE = '/assets/logo.jpg';
const DESCRIPTION_MAX = 160;

const absolute = (url) => (!url ? '' : /^https?:\/\//.test(url) ? url : `${SITE_URL}${url}`);

// A description is one plain line, cut at a word boundary so the snippet in
// search results does not end mid-word.
function clip(text, max = DESCRIPTION_MAX) {
  const flat = String(text ?? '').replace(/\s+/g, ' ').trim();
  if (flat.length <= max) return flat;
  const cut = flat.slice(0, max - 1);
  return `${cut.slice(0, cut.lastIndexOf(' ') > 80 ? cut.lastIndexOf(' ') : cut.length).replace(/[,;:.\s]+$/, '')}…`;
}

// Content dates are free text ("14 March 2026", "Sunday, 11 October 2026 ·
// 9am – 3pm", "18th May, 2025"). Structured data needs ISO, so take the date part and let Date
// read it; anything it cannot read is left out rather than guessed.
export function isoDate(text) {
  const part = String(text ?? '')
    .split('·')[0]
    .replace(/^[A-Za-z]+day,\s*/, '')
    .replace(/(\d)(st|nd|rd|th)\b/gi, '$1')
    .replace(/,/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const m = part.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/);
  if (!m) return '';
  const month = [
    'january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december',
  ].indexOf(m[2].toLowerCase());
  if (month < 0) return '';
  return `${m[3]}-${String(month + 1).padStart(2, '0')}-${m[1].padStart(2, '0')}`;
}

function firstImage(...candidates) {
  for (const c of candidates.flat()) {
    const url = typeof c === 'string' ? c : c?.image;
    if (url) return url;
  }
  return '';
}

/* ---------------- JSON-LD ---------------- */

const ORG_ID = `${SITE_URL}/#organization`;
const SITE_ID = `${SITE_URL}/#website`;

function organization(settings, founders) {
  const address = (settings.addressLines ?? []).join(' ');
  const postalCode = address.match(/\b\d{6}\b/)?.[0];
  return {
    '@type': 'NGO',
    '@id': ORG_ID,
    name: settings.name,
    slogan: settings.tagline,
    url: `${SITE_URL}/`,
    logo: absolute('/assets/logo-mark.png'),
    image: absolute(settings.shareImage || DEFAULT_IMAGE),
    description: clip(settings.seoDescription),
    address: {
      '@type': 'PostalAddress',
      streetAddress: address.replace(/,?\s*Telangana.*$/, ''),
      addressRegion: /telangana/i.test(address) ? 'Telangana' : undefined,
      postalCode,
      addressCountry: 'IN',
    },
    telephone: settings.phone || undefined,
    email: settings.email || undefined,
    sameAs: settings.instagramUrl ? [settings.instagramUrl] : undefined,
    areaServed: 'Ranga Reddy district, Telangana, India',
    founder: (founders?.items ?? [])
      .filter((f) => f.name)
      .map((f) => ({
        '@type': 'Person',
        name: f.name,
        jobTitle: f.role || undefined,
        image: f.image || undefined,
      })),
  };
}

function breadcrumbs(trail) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((t, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: t.name,
      item: `${SITE_URL}${t.path}`,
    })),
  };
}

// Only events whose date can be read become schema.org Events: a startDate is
// required, and a wrong one is worse than none.
function eventLd({ name, date, description, image, place, url }) {
  const startDate = isoDate(date);
  if (!startDate) return null;
  return {
    '@type': 'Event',
    name,
    startDate,
    description: clip(description, 300) || undefined,
    image: image ? [absolute(image)] : undefined,
    url,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    isAccessibleForFree: true,
    location: {
      '@type': 'Place',
      name: place || 'Ranga Reddy district',
      address: { '@type': 'PostalAddress', addressRegion: 'Telangana', addressCountry: 'IN' },
    },
    organizer: { '@id': ORG_ID },
  };
}

// JSON.stringify drops undefined, which is how optional fields above vanish.
export function jsonLdText(graph) {
  return JSON.stringify({ '@context': 'https://schema.org', '@graph': graph }).replace(
    /</g,
    '\\u003c',
  );
}

/* ---------------- per route ---------------- */

// `data` holds whichever content blocks the page loaded; settings is always
// among them. Returns everything the <head> needs for that route.
export function pageMeta({ page = 'home', eventId = null, notFound = false, data }) {
  const { settings } = data;
  const block = data[page] ?? {};
  const label = PAGES.find((p) => p.id === page)?.label ?? '';
  const pagePath = PAGES.find((p) => p.id === page)?.path ?? '/';

  let title;
  let description;
  let path = pagePath;
  let image = '';
  let type = 'website';
  const graph = [organization(settings, data.founders)];
  const trail = [{ name: 'Home', path: '/' }];

  const event = page === 'events' && eventId ? (block.items ?? []).find((e) => e.id === eventId) : null;

  if (page === 'home') {
    title = block.metaTitle || `${settings.name} — ${settings.tagline}`;
    description = block.metaDescription || settings.seoDescription || block.lead;
    image = firstImage(block.slides);
    graph.push({
      '@type': 'WebSite',
      '@id': SITE_ID,
      url: `${SITE_URL}/`,
      name: settings.name,
      publisher: { '@id': ORG_ID },
      inLanguage: 'en-IN',
    });
  } else if (event) {
    path = `/events/${event.id}`;
    title = `${event.title} — ${settings.name}`;
    description = event.blurb || event.detail;
    image = firstImage(event.image, event.photos);
    type = 'article';
    trail.push({ name: label, path: pagePath }, { name: event.title, path });
    const ld = eventLd({
      name: event.title,
      date: event.date,
      description: event.detail || event.blurb,
      image,
      place: event.place,
      url: `${SITE_URL}${path}`,
    });
    if (ld) graph.push(ld);
  } else {
    title = `${block.metaTitle || label} — ${settings.name}`;
    description = block.metaDescription || block.lead;
    trail.push({ name: label, path: pagePath });

    if (page === 'events') {
      image = firstImage((block.items ?? []).map((e) => e.image));
      graph.push({
        '@type': 'ItemList',
        itemListElement: (block.items ?? [])
          .filter((e) => e.id)
          .map((e, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            url: `${SITE_URL}/events/${e.id}`,
            name: e.title,
          })),
      });
    } else if (page === 'camps') {
      image = firstImage(block.items);
      for (const c of block.items ?? []) {
        const ld = eventLd({
          name: `Free health camp — ${c.village}`,
          date: c.date,
          description: [c.venue, (c.services ?? []).join(', '), c.specialists].filter(Boolean).join('. '),
          image: c.image,
          place: [c.venue, c.village].filter(Boolean).join(', '),
          url: `${SITE_URL}${pagePath}`,
        });
        if (ld) graph.push(ld);
      }
    } else if (page === 'cultural') {
      image = firstImage(block.items);
    } else if (page === 'contact') {
      graph.push({
        '@type': 'ContactPage',
        url: `${SITE_URL}${pagePath}`,
        name: title,
        about: { '@id': ORG_ID },
      });
    } else if (page === 'gallery') {
      graph.push({
        '@type': 'ImageGallery',
        url: `${SITE_URL}${pagePath}`,
        name: title,
        about: { '@id': ORG_ID },
      });
    }
  }

  if (trail.length > 1) graph.push(breadcrumbs(trail));

  return {
    title,
    description: clip(description || settings.seoDescription),
    // An unknown path renders the home page (or the events list) rather than
    // an error. It must not be indexed as a duplicate of it.
    noindex: notFound,
    canonical: `${SITE_URL}${path}`,
    image: absolute(image || settings.shareImage || DEFAULT_IMAGE),
    type,
    siteName: settings.name,
    jsonLd: jsonLdText(graph),
  };
}

/* ---------------- the <head> ---------------- */

// [identifying attribute, its value, content]
function tagList(meta) {
  return [
    ['name', 'description', meta.description],
    ['property', 'og:type', meta.type],
    ['property', 'og:site_name', meta.siteName],
    ['property', 'og:locale', 'en_IN'],
    ['property', 'og:title', meta.title],
    ['property', 'og:description', meta.description],
    ['property', 'og:url', meta.canonical],
    ['property', 'og:image', meta.image],
    ['name', 'twitter:card', 'summary_large_image'],
    ['name', 'twitter:title', meta.title],
    ['name', 'twitter:description', meta.description],
    ['name', 'twitter:image', meta.image],
  ];
}

const escAttr = (v) =>
  String(v ?? '').replaceAll('&', '&amp;').replaceAll('"', '&quot;').replaceAll('<', '&lt;');

// The same tags as markup, for the build to write into static HTML.
export function headMarkup(meta) {
  return [
    `<title>${escAttr(meta.title)}</title>`,
    ...tagList(meta).map(([attr, key, value]) => `<meta ${attr}="${key}" content="${escAttr(value)}" />`),
    `<link rel="canonical" href="${escAttr(meta.canonical)}" />`,
    meta.noindex ? '<meta name="robots" content="noindex, follow" />' : '',
    `<script type="application/ld+json" id="ld-json">${meta.jsonLd}</script>`,
  ]
    .filter(Boolean)
    .join('\n    ');
}

export function setRobots(content) {
  let tag = document.querySelector('meta[name="robots"]');
  if (!content) {
    tag?.remove();
    return;
  }
  if (!tag) {
    tag = document.createElement('meta');
    tag.name = 'robots';
    document.head.appendChild(tag);
  }
  tag.content = content;
}

// Updates the live document in place after an in-app navigation, creating
// any tag the shell did not already carry.
export function applyMeta(meta) {
  document.title = meta.title;
  for (const [attr, key, value] of tagList(meta)) {
    let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute(attr, key);
      document.head.appendChild(tag);
    }
    tag.content = value ?? '';
  }

  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = meta.canonical;

  let ld = document.getElementById('ld-json');
  if (!ld) {
    ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.id = 'ld-json';
    document.head.appendChild(ld);
  }
  ld.textContent = meta.jsonLd;

  setRobots(meta.noindex ? 'noindex, follow' : '');
}

/* ---------------- build-time route list ---------------- */

// Every indexable URL: the visible pages plus each event's photo page. Hidden
// pages are left out, as they redirect home.
export function routes(data) {
  const list = visiblePages(data.settings).map((p) => ({ page: p.id, path: p.path, eventId: null }));
  if (list.some((r) => r.page === 'events')) {
    for (const e of data.events?.items ?? []) {
      if (e.id) list.push({ page: 'events', path: `/events/${e.id}`, eventId: e.id });
    }
  }
  return list;
}
